import { useMemo } from "react";
import { POSTS, CATEGORY_LIST } from "@/lib/blogMeta.generated";
import CoverImage from "@/components/CoverImage";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

interface ServiceBlogSectionProps {
  categorySlug: string;
  isMobile: boolean;
}

export default function ServiceBlogSection({ categorySlug, isMobile }: ServiceBlogSectionProps) {
  const articles = useMemo(() => {
    const filtered = POSTS.filter(p => p.categories.includes(categorySlug));
    // Return up to 3 articles
    return filtered.slice(0, 3).map(p => ({
      category: CATEGORY_LIST.find(c => c.slug === categorySlug)?.name ?? categorySlug,
      title: p.title,
      excerpt: p.excerpt || p.metaDescription,
      readTime: p.readTime,
      href: `/blog/${p.slug}`,
      post: p,
    }));
  }, [categorySlug]);

  // Don't render if no articles exist for this category yet
  if (articles.length === 0) return null;

  return (
    <section className="section-grid" style={{ background: "#0c0c0c", padding: isMobile ? "60px 20px" : "100px 72px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "24px", marginBottom: "56px" }}>
          <h2 style={{ color: "white", fontSize: "clamp(32px,5vw,48px)", fontWeight: 700, fontFamily: ff, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, textAlign: "left" as const }}>
            From the<br /><span style={{ color: cyan }}>Indexed blog.</span>
          </h2>
          <a href={`/blog/category/${categorySlug}`}
            style={{ color: cyan, fontSize: "16px", fontWeight: 600, fontFamily: ff, textDecoration: "none", transition: "opacity 0.2s" }}
            onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
            onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
            Read all →
          </a>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "18px" }}>
          {/* Featured article (first) */}
          <a href={articles[0].href} style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", overflow: "hidden", textDecoration: "none", display: "flex", flexDirection: "column", transition: "border-color 0.2s, transform 0.2s", gridRow: articles.length > 1 ? "span 2" : undefined }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.25)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
            <div style={{ position: "relative", overflow: "hidden" }}>
              {/* CoverImage, not a bare <img>: a cover that 404s becomes a
                  category-tinted panel instead of a broken-image icon. */}
              <div style={{ height: "260px" }}>
                <CoverImage post={articles[0].post} imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to bottom, transparent, #111111)" }} />
            </div>
            <div style={{ padding: "32px", flex: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
              <span style={{ color: cyan, fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase" as const }}>{articles[0].category}</span>
              <h3 style={{ color: "white", fontSize: "22px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.3 }}>{articles[0].title}</h3>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.65, margin: 0, flex: 1 }}>{articles[0].excerpt}</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "13px", fontFamily: ff }}>{articles[0].readTime}</span>
                <span style={{ color: cyan, fontSize: "14px", fontWeight: 600, fontFamily: ff }}>Read more →</span>
              </div>
            </div>
          </a>
          {/* Secondary articles */}
          {articles.slice(1).map((art, i) => (
            <a key={i} href={art.href} style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", overflow: "hidden", textDecoration: "none", display: "flex", transition: "border-color 0.2s, transform 0.2s" }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.25)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
              <div style={{ width: "140px", flexShrink: 0 }}>
                <CoverImage post={art.post} imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "8px" }}>
                <span style={{ color: cyan, fontSize: "10px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase" as const }}>{art.category}</span>
                <h3 style={{ color: "white", fontSize: "16px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.3 }}>{art.title}</h3>
                <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.6, margin: 0, flex: 1 }}>{art.excerpt}</p>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "4px" }}>
                  <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px", fontFamily: ff }}>{art.readTime}</span>
                  <span style={{ color: cyan, fontSize: "13px", fontWeight: 600, fontFamily: ff }}>Read more →</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
